import { fileService, FileUploadError } from '@/lib/files/service';
import { logger } from '@/lib/logger';

export interface AttachWithRollbackParams {
  orgId: number;
  uploadedFileId: number;
  entityId: string; // for log context
  logComponent: string; // e.g. 'files' | 'task-attachments'
  attach: () => Promise<unknown>;
}

/**
 * Run `attach()`; if it fails, roll back the now-orphaned upload ONLY when the
 * server definitively rejected the attach (FileUploadError with a defined
 * `status`). On a network/timeout error the response was lost and the attach may
 * have committed server-side — deleting the blob would strand the record's
 * association on a missing file — so the upload is left in place. Cleanup is
 * best-effort: a failed delete is logged, the original attach error is re-thrown.
 *
 * Invariant: a defined `status` is treated as "association did not commit". This
 * rests on the attach endpoint being fully transactional (a 5xx returned AFTER a
 * commit would wrongly trigger a delete). Preserved from useTaskAttachments.
 */
export async function attachWithRollback(
  p: AttachWithRollbackParams
): Promise<void> {
  try {
    await p.attach();
  } catch (attachError) {
    const serverRejected =
      attachError instanceof FileUploadError &&
      attachError.status !== undefined;
    if (serverRejected) {
      try {
        await fileService.deleteFile(p.orgId, p.uploadedFileId);
      } catch (cleanupError) {
        logger.error(
          'Failed to clean up orphaned upload after attach failure',
          cleanupError instanceof Error ? cleanupError : undefined,
          {
            component: p.logComponent,
            orgId: p.orgId,
            entityId: p.entityId,
            uploadedFileId: p.uploadedFileId,
          }
        );
      }
    } else {
      // Indeterminate error (network/timeout): the attach may have committed
      // server-side, so we do NOT delete. Record the upload id so a genuinely
      // orphaned blob from this path is still locatable for reconciliation.
      logger.warn(
        'Attach failed with an indeterminate error; upload left in place and may be orphaned',
        {
          component: p.logComponent,
          orgId: p.orgId,
          entityId: p.entityId,
          uploadedFileId: p.uploadedFileId,
        }
      );
    }
    throw attachError;
  }
}
